import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Box, Avatar, Menu, MenuItem,
  ListItemIcon, Skeleton, Link, Breadcrumbs
} from '@mui/material';
import {
  Brightness4, Brightness7, Person, ChevronRight, Logout as LogoutIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGrading } from '../contexts/GradingContext';
import { useTheme } from '../hooks/useTheme';
import { useNavigationBlock } from '../contexts/NavigationBlockContext';
import useUserPhoto from '../hooks/useUserPhoto';

const BreadNav: React.FC = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const { gradingState } = useGrading();
  const { mode, toggleMode } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { photoUrl, loading: photoLoading } = useUserPhoto();

  // Navigation blocking context
  const { isBlocked, requestNavigation } = useNavigationBlock();

  useEffect(() => {
    // Location changed
  }, [location]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); handleClose(); };

  // Centralized handler for all breadcrumb navigation
  const handleCrumbClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    if (isBlocked) {
      requestNavigation(path);
    } else {
      navigate(path);
    }
  };

  // Breadcrumb logic
  let breadcrumbs: React.ReactElement[] = [];

  if (location.pathname === '/grading') {
    const roleplayName = gradingState?.roleplayName;
    const dateCompleted = gradingState?.dateCompleted;
    const gradingComplete = gradingState?.status === 'completed';
    breadcrumbs = [
      gradingComplete ? (
        <Link
          underline="hover"
          color="inherit"
          href="/"
          key="home"
          onClick={e => handleCrumbClick(e, '/')}
        >
          <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 0.5 }} />
            Roleplay
          </Typography>
        </Link>
      ) : (
        <Typography key="home" sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 0.5 }} />
          Roleplay
        </Typography>
      ),
      gradingComplete ? (
        <Link
          underline="hover"
          color="inherit"
          href="/feedback"
          key="feedback"
          onClick={e => handleCrumbClick(e, '/feedback')}
        >
          <Typography sx={{ color: 'text.primary' }}>
            Feedback
          </Typography>
        </Link>
      ) : (
        <Typography key="feedback" sx={{ color: 'text.disabled' }}>
          Feedback
        </Typography>
      ),
      <Typography key="grading" sx={{ color: 'text.primary' }}>
        Feedback: {roleplayName || 'Unknown'} {dateCompleted || ''}
      </Typography>
    ];
  } else if (location.pathname.startsWith('/call')) {
    breadcrumbs = [
      <Link
        underline="hover"
        color="inherit"
        href="/"
        key="home"
        onClick={e => handleCrumbClick(e, '/')}
      >
        <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 0.5 }} />
          Roleplay
        </Typography>
      </Link>,
      <Link
        underline="hover"
        color="inherit"
        href="/assessment"
        key="assessment"
        onClick={e => handleCrumbClick(e, '/assessment')}
      >
        <Typography sx={{ color: 'text.primary' }}>
          Assessments
        </Typography>
      </Link>,
      <Typography key="coaching" sx={{ color: 'text.primary' }}>
        Coaching
      </Typography>
    ];
  } else if (location.pathname.startsWith('/assessment')) {
    breadcrumbs = [
      <Link
        underline="hover"
        color="inherit"
        href="/"
        key="home"
        onClick={e => handleCrumbClick(e, '/')}
      >
        <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 0.5 }} />
          Roleplay
        </Typography>
      </Link>,
      <Typography key="assessment" sx={{ color: 'text.primary' }}>
        Assessments
      </Typography>
    ];
  } else if (location.pathname.startsWith('/feedback')) {
    const feedbackPathParts = location.pathname.split('/').filter(Boolean);
    const isSpecificFeedback = feedbackPathParts.length > 1 && feedbackPathParts[0] === 'feedback' && feedbackPathParts[1];

    if (isSpecificFeedback) {
      // Handle /feedback/:id
      breadcrumbs = [
        <Link
          underline="hover"
          color="inherit"
          href="/"
          key="home"
          onClick={e => handleCrumbClick(e, '/')}
        >
          <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 0.5 }} />
            Roleplay
          </Typography>
        </Link>,
        <Link
          underline="hover"
          color="inherit"
          href="/feedback"
          key="feedback"
          onClick={e => handleCrumbClick(e, '/feedback')}
        >
          <Typography sx={{ color: 'text.primary' }}>
            Feedback
          </Typography>
        </Link>,
        <Typography key="feedback-report" sx={{ color: 'text.primary' }}>
          Feedback Report
        </Typography>
      ];
    } else {
      // Handle /feedback
      breadcrumbs = [
        <Link
          underline="hover"
          color="inherit"
          href="/"
          key="home"
          onClick={e => handleCrumbClick(e, '/')}
        >
          <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 0.5 }} />
            Roleplay
          </Typography>
        </Link>,
        <Typography key="feedback" sx={{ color: 'text.primary' }}>
          Feedback
        </Typography>
      ];
    }
  } else if (location.pathname.startsWith('/admin')) {
    breadcrumbs = [
      <Link
        underline="hover"
        color="inherit"
        href="/"
        key="home"
        onClick={e => handleCrumbClick(e, '/')}
      >
        <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 0.5 }} />
          Roleplay
        </Typography>
      </Link>,
      <Typography key='admin' sx={{ color: 'text.primary'}}>
        Admin
      </Typography>
    ];
  }
  else {
    breadcrumbs = [
      <Link
        underline="hover"
        color="inherit"
        href="/"
        key="home"
        onClick={e => handleCrumbClick(e, '/')}
      >
        <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 0.5 }} />
          Roleplay
        </Typography>
      </Link>
    ];
  }

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Breadcrumbs */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Breadcrumbs separator={<ChevronRight />} aria-label="breadcrumb">
            {breadcrumbs}
          </Breadcrumbs>
        </Box>

        {/* Avatar/Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
          {authLoading || photoLoading ? (
            <>
              <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
              <Skeleton variant="text" width={80} height={20} />
            </>
          ) : (
            <>
              <Avatar
                src={photoUrl || undefined}
                sx={{ cursor: 'pointer', mr: 1 }}
                onClick={handleMenu}
              >
                {(!photoUrl && user?.name?.charAt(0)) || 'U'}
              </Avatar>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user?.name}</Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography variant="caption">{user?.username}</Typography>
                </MenuItem>
                <MenuItem onClick={toggleMode}>
                  <ListItemIcon>
                    {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
                  </ListItemIcon>
                  Theme: {mode === 'dark' ? 'Light' : 'Dark'}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default BreadNav;